import { Button, Device, Devices, Targets } from "@app/shared";
import { useEffect, useState } from "react";
import ShakaPlayer from "shaka-player-react";
import 'shaka-player-react/dist/controls.css';

import { useInterval } from "react-use";
import { useConfirmDialog, useFetch, useTargetPhoto } from "../../hooks";
import { SkeletonGrid } from "./loading";
import { WebRtcPlayer } from "@app/shared/src/components/WebRtcPlayer";

type DeviceVideoStreamProps = {
    device: Device | undefined;
};

export const DeviceVideoStream: React.FC<DeviceVideoStreamProps> = ({ device }) => {
    //Display the last target image in the preview
    const [lastTarget, , lastTargetHelper] = useFetch(() => device ? Targets.getLastTargetWithPhotoForDevice(device.id) : Promise.resolve(undefined));
    const [streamUrl, setStreamUrl] = useState<string>();
    const [showCrosshair, setShowCrosshair] = useState<boolean>(false);
    const confirm = useConfirmDialog();
    const { photoUrl, hasPhoto } = useTargetPhoto(lastTarget?.externalId, device?.id, device?.organizationId);

    //Keep polling paused while the tab is in the background. A device page left open otherwise
    //keeps asking for a snapshot nobody is looking at, for as long as the tab exists.
    const [isTabVisible, setIsTabVisible] = useState(() => !document.hidden);
    useEffect(() => {
        const onVisibilityChange = () => setIsTabVisible(!document.hidden);
        document.addEventListener("visibilitychange", onVisibilityChange);
        return () => document.removeEventListener("visibilitychange", onVisibilityChange);
    }, []);

    //Only refresh the snapshot preview while it is actually the thing on screen, and never while
    //the previous request is still outstanding — an unguarded tick stacks a fresh request every
    //5s when the server is slow to answer, and each one holds a connection until it resolves.
    const { isLoading: lastTargetIsLoading, isRefreshing: lastTargetIsRefreshing, refreshData: refreshLastTarget } = lastTargetHelper;
    useInterval(
        () => {
            if (lastTargetIsLoading || lastTargetIsRefreshing) return;
            refreshLastTarget();
        },
        //This is not displayed if the streamUrl has been set
        !streamUrl && isTabVisible ? 5000 : null
    );

    const handleStartStreamOnClick = async () => {
        const newUrl = await Devices.getDeviceStream(device!.id);
        if (!newUrl) {
            confirm.show("Error", "Error requesting video stream.  Please try again later.", "Ok");
            console.log("invalid stream url: ", newUrl);
        } else {
            setStreamUrl(newUrl);
        }
    };


    const isLegacy = (device?.firmwareVersion?.startsWith("1.") && device?.enableWebRtc !== true) === true;


    if (!device) {
        return <SkeletonGrid rows={2} cols={1} />
    }

    return (
        <div className="text-center max-w-6xl mx-auto">
            {streamUrl &&
                <div className="bg-background-left-nav relative">
                    {showCrosshair && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                            <div className="relative">
                                {/* Top arm */}
                                <div className="absolute w-0.5 h-2.5 bg-red-500 -translate-x-0.5 -translate-y-3.5"></div>
                                {/* Bottom arm */}
                                <div className="absolute w-0.5 h-2.5 bg-red-500 -translate-x-0.5 translate-y-0.5"></div>
                                {/* Left arm */}
                                <div className="absolute w-2.5 h-0.5 bg-red-500 -translate-y-0.5 -translate-x-3.5"></div>
                                {/* Right arm */}
                                <div className="absolute w-2.5 h-0.5 bg-red-500 -translate-y-0.5 translate-x-0.5"></div>
                            </div>
                        </div>
                    )}
                    {isLegacy ?
                        <ShakaPlayer autoPlay src={streamUrl} /> :
                        <WebRtcPlayer device={device} />}
                </div>
            }
            {!streamUrl && hasPhoto &&
                <img src={photoUrl} className="mx-auto my-auto bg-background-body" alt="Last Target" style={{ minHeight: 480 }} />
            }
            {streamUrl &&
                <div className="mt-2">
                    <Button onClick={() => setShowCrosshair(!showCrosshair)} className="mx-auto">
                        {showCrosshair ? 'Hide' : 'Show'} Center
                    </Button>
                </div>
            }
            {!streamUrl &&
                <div>
                    <Button onClick={handleStartStreamOnClick} className="mx-auto">Request Video Feed</Button>
                </div>
            }

            {confirm.renderDialog()}
        </div>
    );

}